import { UserRole } from '@citrite/sf-api';
import { esignBlocks, esignDrawers } from '@sharefiledev/esign-pilet-sdk';
import { SendForSignatureActionIcon } from '@sharefiledev/flow-web';
import type { PiletApi, PiletUserContext } from '@sharefiledev/sharefile-appshell';
import { EsignExtensionSlots, SEND_FOR_SIGNATURE_ENTITLEMENT } from './constants';
import { DashboardSignButton } from './Dashboard/DashboardSign/DashboardSignButton';
import { DashboardSignButtonBlock } from './Dashboard/DashboardSign/DashboardSignButtonBlock';
import { acquireAccessToken } from './data/acquireAccessToken';
import { configureESignatureClient } from './data/eSignatureClient';
import { getRSConfig, isLocalRSSetup } from './data/rightSignatureConfiguration';
import {
	AsyncClientSignerTAPDrawer,
	AsyncDocumentSendSignatureRequestDrawer,
	AsyncSendForSignatureDrawer,
	AsyncSignatureRequestTAPDrawer,
} from './Drawers';
import { SignatureRequestDrawerFooter } from './Drawers/DocumentSendSignatureRequestDrawer/Footer/SignatureRequestDrawerFooter';
import { SignatureRequestDrawerHeader } from './Drawers/DocumentSendSignatureRequestDrawer/Header/SignatureRequestDrawerHeader';
import {
	AsyncSendForSignCVContextActionExtenison,
	AsyncSendForSignFileListContextActionExtension,
	AsyncSignYourselfCVContextActionExtension,
	AsyncSignYourselfFileListContextActionExtension,
} from './Extensions';
import { setLogger } from './logger';
import {
	AsyncCreateDocument,
	AsyncDashboard,
	AsyncNewDocumentPage,
	AsyncPrepareDocumentPage,
	AsyncPreviewPage,
	AsyncRedirectToTemplatesPage,
	AsyncReviewAndSendDocumentPage,
	AsyncSignatureRequestDetailsPage,
	AsyncSignatureRequestHandler,
	AsyncSignerPage,
	AsyncStartDocumentPage,
} from './Pages';
import {
	RegisterSendForSignature,
	SendForSignatureFileListContextAction,
	SignatureDetailsFileListItem,
	SignYourselfFileListContextAction,
} from './RegisterSendForSignature';
import {
	createDocumentPath,
	newDocumentPath,
	prepareDocumentPath,
	previewPagePath,
	reviewAndSendDocumentPath,
	signatureRequestItemPath,
	signDashboardPath,
	signerPagePath,
	startDocumentPath,
	startDocumentWorkflowWithFilePath,
	templatesPath,
} from './routes';
import { t } from './util';
import {
	IsProductLedGrowthEnabled,
	isSendForSignatureActionAvailable,
	showEsignPilet,
} from './util/access';

export function setup(app: PiletApi) {
	setLogger(app.sf.getLogger());

	const rsConfig = getRSConfig(app.sf.workflowsContext.get());
	const { account, entitlements, user }: PiletUserContext = app.sf.piletUserContext.get();
	const isEmployee = user.Roles?.includes(UserRole.Employee);

	if (isLocalRSSetup()) {
		configureESignatureClient({
			baseUrl: rsConfig.RIGHTSIGNATURE_BASE_URL,
			useBffAuthProxy: false,
			getAccessToken: () => acquireAccessToken(rsConfig.RS4_CLIENT_ID),
		});
	} else {
		let appOrigin = window.origin;
		if (typeof app.meta.config.apiOrigin === 'string') {
			appOrigin = app.meta.config.apiOrigin;
		}

		configureESignatureClient({
			baseUrl: `${appOrigin}/io/esign`,
			useBffAuthProxy: true,
		});
	}

	// Register Unauthenticated Pages before checking for entitlements
	app.registerPage(signerPagePath, () => <AsyncSignerPage />, {
		unauthenticated: true,
	});
	app.registerPage(previewPagePath, () => <AsyncPreviewPage />, {
		unauthenticated: true,
	});

	app.registerPage(startDocumentWorkflowWithFilePath, () => (
		<AsyncSignatureRequestHandler piletApi={app} />
	));

	// This code block registers a drawer (`signatureRequestDrawer`) that will be consumed by the Task Aggregator Platform (TAP).
	// The drawer is responsible for rendering the Signer Page (`AsyncClientSignerTAPDrawer`) with the provided options and interaction handlers.
	app.registerDrawer(
		esignDrawers.signatureRequestDrawer,
		({ options, cancelInteraction }) => {
			return (
				<AsyncClientSignerTAPDrawer
					{...options}
					cancelInteraction={cancelInteraction}
					piletApi={app}
				/>
			);
		},
		{
			drawerProps: {
				width: '100%',
				zIndex: 5,
				styles: {
					header: {
						display: 'none',
					},
					body: {
						padding: 0,
					},
				},
			},
		}
	);

	const sendForSignatureStatus = entitlements.getEntitlementStatus(
		SEND_FOR_SIGNATURE_ENTITLEMENT
	);
	const isPLGEnabled = IsProductLedGrowthEnabled(app);

	if (!showEsignPilet(account, user, sendForSignatureStatus)) {
		return;
	}

	registerExtensions(app);

	app.registerPage(startDocumentPath, () => <AsyncStartDocumentPage piletApi={app} />);

	app.registerPage(prepareDocumentPath, () => (
		<AsyncPrepareDocumentPage piletApi={app} />
	));

	app.registerPage(reviewAndSendDocumentPath, () => (
		<AsyncReviewAndSendDocumentPage piletApi={app} />
	));

	app.registerPage(newDocumentPath, () => <AsyncNewDocumentPage piletApi={app} />);

	app.registerPage(createDocumentPath, () => <AsyncCreateDocument piletApi={app} />);

	app.registerExtension(
		'urn:sfextslot:sharefile-appshell:folder:item-context-actions',
		({ params }) => (
			<AsyncSendForSignFileListContextActionExtension params={params} piletApi={app} />
		),
		{
			piletSortWeight: 300,
			isAvailable: params =>
				SendForSignatureFileListContextAction.isAvailable(
					params,
					app.sf.piletUserContext.get().account,
					app.sf.piletUserContext.get().user,
					isPLGEnabled
				),
		}
	);

	app.registerExtension(
		'urn:sfextslot:sharefile-appshell:folder:item-context-actions',
		({ params }) => (
			<AsyncSignYourselfFileListContextActionExtension params={params} piletApi={app} />
		),
		{
			piletSortWeight: 2000,
			isAvailable: params =>
				SignYourselfFileListContextAction.isAvailable(
					params,
					app.sf.piletUserContext.get().account,
					app.sf.piletUserContext.get().user,
					isPLGEnabled
				),
		}
	);

	const isSignatureActionAvailable = isSendForSignatureActionAvailable(
		app.sf.piletUserContext.get().user
	);

	if (isEmployee && isSignatureActionAvailable) {
		app.registerExtension(
			'urn:sfextslot:sharefile-contentviewer:context-actions',
			({ params }) => (
				<AsyncSendForSignCVContextActionExtenison params={params} piletApi={app} />
			),
			{
				piletSortWeight: 800,
			}
		);

		app.registerExtension(
			'urn:sfextslot:sharefile-contentviewer:context-actions',
			({ params }) => (
				<AsyncSignYourselfCVContextActionExtension params={params} piletApi={app} />
			),
			{
				piletSortWeight: 900,
			}
		);
	}

	if (isSignatureActionAvailable) {
		// Pages
		app.registerPage(signDashboardPath, () => <AsyncDashboard piletApi={app} />);
		app.registerPage(signatureRequestItemPath, () => (
			<AsyncSignatureRequestDetailsPage piletApi={app} />
		));
		app.registerPage(templatesPath, () => (
			<AsyncRedirectToTemplatesPage piletApi={app} />
		));

		// Extensions
		app.registerExtension(
			'urn:sfextslot:sharefile-appshell:folder:signaturedetails',
			({ params }) => <SignatureDetailsFileListItem {...params} />
		);

		// ShareFile left Nav Component
		app.sf.registerLeftNavComponent({
			title: () => t('dashboard.esignLeftNav'),
			href: signDashboardPath,
			isAvailable: () => isSignatureActionAvailable,
			icon: () => <SendForSignatureActionIcon style={{ opacity: '0.5' }} />,
			weight: 350,
		});

		// Drawers
		app.registerDrawer(
			'urn:sfdrawer:task-aggregator:signature_request',
			props => {
				return <AsyncSignatureRequestTAPDrawer {...props} piletApi={app} />;
			},
			{
				drawerProps: {
					width: '100%',
					zIndex: 5,
					styles: {
						body: {
							padding: 0,
						},
					},
				},
			}
		);
	}

	//Send for Signature Drawer
	app.registerDrawer(
		esignDrawers.sendForSignatureDrawer,
		props => {
			return <AsyncSendForSignatureDrawer {...props} piletApi={app} />;
		},
		{
			drawerProps: {
				width: '0%',
				height: '0%',
				zIndex: -1,
				mask: false,
				styles: {
					body: {
						padding: 0,
					},
				},
			},
		}
	);

	// Send for Signature from Create Template Drawer
	// This drawer is used when the user is creating a document from a template
	// isCreateFromTemplate is passed as a prop to render the Template selection modal on load
	app.registerDrawer(
		esignDrawers.createFromTemplateDrawer,
		props => {
			return (
				<AsyncSendForSignatureDrawer isCreateFromTemplate {...props} piletApi={app} />
			);
		},
		{
			drawerProps: {
				width: '0%',
				height: '0%',
				zIndex: -1,
				mask: false,
				styles: {
					body: {
						padding: 0,
					},
				},
			},
		}
	);

	// Document Send for Signature Drawer
	// This drawer is used when the user is sending document template for signature.
	app.registerDrawer(
		esignDrawers.quickSendForSignatureDrawer,
		({ options, cancelInteraction }) => {
			return (
				<AsyncDocumentSendSignatureRequestDrawer
					piletApi={app}
					cancelInteraction={cancelInteraction}
					{...options}
				/>
			);
		},
		{
			drawerProps: {
				// Define this components
				title: <SignatureRequestDrawerHeader />,
				footer: <SignatureRequestDrawerFooter />,
				mask: false,
				styles: {
					body: {
						padding: 0,
					},
				},
			},
		}
	);

	app.registerExtension(EsignExtensionSlots.SignFromDashboardButton, ({ params }) => (
		<DashboardSignButton {...params} />
	));

	registerBlocks(app);
}

function registerExtensions(app: PiletApi) {
	RegisterSendForSignature(app, 'urn:sfextslot:sharefile-appshell:dashboard:shortcuts');
}

function registerBlocks(app: PiletApi) {
	app.registerExtension(esignBlocks.signFromDashboardButtonBlock, ({ params }) => (
		<DashboardSignButtonBlock piletApi={app} {...params} />
	));
}
